import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Table,
  Input,
  Space,
  Checkbox,
  Tooltip,
  Popconfirm,
  message,
  Typography,
  Tag,
  Select,
  Modal
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  SettingOutlined,
  ExportOutlined
} from "@ant-design/icons";
import warehouseApi from "../api/warehouseApi";
import "./WarehousesListTable.scss";

const { Text } = Typography;
const { Search } = Input;

const WarehousesListTable = ({
  onWarehouseSelect,
  onCreateWarehouse,
  onEditWarehouse
}) => {
  const history = useHistory();
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteTooltipVisible, setDeleteTooltipVisible] = useState({});

  // Load warehouses on mount
  useEffect(() => {
    loadWarehouses();
  }, []);

  // Also reload when the component becomes visible (when returning from edit page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log("Page became visible, reloading warehouses...");
        loadWarehouses();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Filter warehouses based on search
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredWarehouses(warehouses);
    } else {
      const filtered = warehouses.filter(
        (warehouse) =>
          warehouse.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          warehouse.address?.toLowerCase().includes(searchText.toLowerCase()) ||
          warehouse.city?.toLowerCase().includes(searchText.toLowerCase()) ||
          warehouse.state?.toLowerCase().includes(searchText.toLowerCase()) ||
          warehouse.savedLocation?.locationName
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          warehouse.savedLocation?.address
            ?.toLowerCase()
            .includes(searchText.toLowerCase())
      );
      setFilteredWarehouses(filtered);
    }
  }, [warehouses, searchText]);

  const loadWarehouses = async () => {
    setLoading(true);
    try {
      const data = await warehouseApi.getWarehouses();
      console.log("🏢 WarehousesListTable - API response:", data);
      console.log("🏢 WarehousesListTable - First warehouse:", data?.[0]);
      console.log(
        "🏢 WarehousesListTable - First warehouse savedLocation:",
        data?.[0]?.savedLocation
      );
      console.log(
        "🏢 WarehousesListTable - First warehouse savedLocation image:",
        data?.[0]?.savedLocation?.image
      );
      setWarehouses(data);
    } catch (error) {
      console.error("Error loading warehouses:", error);
      message.error("Failed to load warehouses");
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (warehouseId, warehouseName) => {
    Modal.confirm({
      title: "Delete Warehouse?",
      icon: null,
      content: (
        <div>
          <p style={{ marginBottom: "12px" }}>
            Are you sure you want to delete <strong>{warehouseName}</strong>?
          </p>
          <p style={{ color: "#ff4d4f", fontWeight: "bold", marginBottom: "8px" }}>
            ⚠️ Warning: This action cannot be undone!
          </p>
          <p style={{ marginBottom: "0" }}>
            All inventory items tied to this warehouse will also be permanently deleted.
          </p>
        </div>
      ),
      okText: "Yes, Delete Warehouse",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await warehouseApi.deleteWarehouse(warehouseId);
          message.success("Warehouse and all inventory deleted successfully");
          loadWarehouses();
        } catch (error) {
          console.error("Error deleting warehouse:", error);
          message.error("Failed to delete warehouse");
        }
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select warehouses to delete");
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} Warehouse${
        selectedRowKeys.length > 1 ? "s" : ""
      }?`,
      icon: null,
      content: (
        <div>
          <p style={{ marginBottom: "12px" }}>
            Are you sure you want to delete {selectedRowKeys.length} warehouse
            {selectedRowKeys.length > 1 ? "s" : ""}?
          </p>
          <p style={{ color: "#ff4d4f", fontWeight: "bold", marginBottom: "8px" }}>
            ⚠️ Warning: This action cannot be undone!
          </p>
          <p style={{ marginBottom: "0" }}>
            All inventory items in these warehouses will also be permanently deleted.
          </p>
        </div>
      ),
      okText: "Yes, Delete All",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) => warehouseApi.deleteWarehouse(id))
          );
          message.success(
            `${selectedRowKeys.length} warehouse(s) and all inventory deleted successfully`
          );
          setSelectedRowKeys([]);
          loadWarehouses();
        } catch (error) {
          console.error("Error deleting warehouses:", error);
          message.error("Failed to delete some warehouses");
        }
      }
    });
  };

  const handleBulkExport = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select warehouses to export");
      return;
    }

    const selectedWarehouses = warehouses.filter((w) => selectedRowKeys.includes(w.id));

    // Create CSV content
    const headers = [
      "Name",
      "Category",
      "Address",
      "City",
      "State",
      "Zip Code",
      "Item Count",
      "Total Value",
      "Status"
    ];
    const csvContent = [
      headers.join(","),
      ...selectedWarehouses.map((w) =>
        [
          `"${w.name}"`,
          `"${w.category || "Distribution"}"`,
          `"${w.address}"`,
          `"${w.city}"`,
          `"${w.state}"`,
          `"${w.zipCode}"`,
          w.itemCount || 0,
          w.totalValue || 0,
          `"${w.status || "Active"}"`
        ].join(",")
      )
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `warehouses_export_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(`Exported ${selectedRowKeys.length} warehouse(s) to CSV`);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      name: record.name
    })
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "#52c41a";
      case "Low Stock":
        return "#fa8c16";
      case "Inactive":
        return "#ff4d4f";
      default:
        return "#d9d9d9";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Distribution":
        return "blue";
      case "Storage":
        return "green";
      case "Manufacturing":
        return "purple";
      case "Retail":
        return "orange";
      case "Other":
        return "default";
      default:
        return "blue";
    }
  };

  const columns = [
    {
      title: "Image",
      key: "image",
      width: 80,
      align: "center",
      render: (_, record) => {
        // Use savedLocation image if available, otherwise fallback to warehouse image
        const imageUrl = record.savedLocation?.image?.url || record.image;

        // Debug: Log image data to console
        console.log("Warehouse image debug:", {
          id: record.id,
          name: record.name,
          savedLocationImage: record.savedLocation?.image?.url,
          warehouseImage: record.image,
          finalImageUrl: imageUrl,
          hasImage: !!imageUrl
        });

        return (
          <div className="warehouse-image">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={record.name}
                className="warehouse-thumbnail"
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9"
                }}
                onLoad={() => {
                  console.log("✅ Image loaded successfully for:", record.name);
                }}
                onError={(e) => {
                  console.error("❌ Image failed to load for:", record.name);
                  console.error("Image URL:", imageUrl);
                  // Hide the image and show placeholder
                  e.target.style.display = "none";
                  const container = e.target.parentNode;
                  const placeholder = container.querySelector(".image-placeholder");
                  if (placeholder) {
                    placeholder.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <div
              className="image-placeholder"
              style={{
                display: imageUrl ? "none" : "flex",
                width: "60px",
                height: "60px",
                backgroundColor: "#f0f0f0",
                borderRadius: "6px",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
                border: "1px solid #d9d9d9"
              }}
            >
              🏢
            </div>
          </div>
        );
      }
    },
    {
      title: "Warehouse Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Text strong className="warehouse-name">
          {text}
        </Text>
      )
    },
    {
      title: "Warehouse Category",
      dataIndex: "category",
      key: "category",
      render: (category) => {
        const categoryName = category || "Distribution";
        return <Tag color={getCategoryColor(categoryName)}>{categoryName}</Tag>;
      }
    },
    {
      title: "Location",
      key: "location",
      sorter: (a, b) =>
        (a.savedLocation?.locationName || a.address || "").localeCompare(
          b.savedLocation?.locationName || b.address || ""
        ),
      render: (_, record) => {
        const savedLocation = record.savedLocation;
        if (!savedLocation) {
          return <Text type="secondary">No location assigned</Text>;
        }
        return (
          <div className="location-info">
            <div className="location-name">
              <Text strong>{savedLocation.locationName || "Unnamed Location"}</Text>
            </div>
            {savedLocation.address && (
              <div className="location-address">
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {savedLocation.address}
                </Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Warehouse" mouseLeaveDelay={0}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEditWarehouse(record);
              }}
              size="small"
              className="action-btn edit-btn"
            />
          </Tooltip>
          <Tooltip
            title="Delete Warehouse"
            open={deleteTooltipVisible[record.id]}
            onOpenChange={(open) => {
              setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: open }));
            }}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              className="action-btn delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: false }));
                handleDelete(record.id, record.name);
              }}
              onMouseEnter={() =>
                setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: true }))
              }
              onMouseLeave={() =>
                setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: false }))
              }
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div className="client-warehouses-list">
      {/* Page Header - consistent with other tabs */}
      <div className="page-header">
        <div className="page-title">
          <h2 className="title">Warehouses</h2>
        </div>
        <div className="page-actions">
          <Button
            icon={<SettingOutlined />}
            onClick={() => history.push("/warehouses/settings")}
            className="settings-btn"
            style={{ marginRight: 12 }}
          >
            Settings
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateWarehouse}
            className="add-warehouse-btn"
          >
            Add Warehouse
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="table-wrapper">
        <div className="table-header">
          <div className="table-controls-left">
            <Space>
              <Search
                placeholder="Search warehouses..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                style={{ width: 250 }}
              />
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  console.log("🔄 Manual refresh triggered");
                  loadWarehouses();
                  message.success("Warehouse data refreshed");
                }}
                className="control-btn"
                title="Refresh"
              />
            </Space>
          </div>

          <div className="table-controls-right"></div>
        </div>

        {/* Bulk Actions Bar */}
        {hasSelected && (
          <div className="bulk-actions-bar">
            <div className="bulk-info">
              <Checkbox
                checked={selectedRowKeys.length === filteredWarehouses.length}
                indeterminate={
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < filteredWarehouses.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRowKeys(filteredWarehouses.map((w) => w.id));
                  } else {
                    setSelectedRowKeys([]);
                  }
                }}
              />
              <Text strong style={{ marginLeft: 8 }}>
                {selectedRowKeys.length}{" "}
                {selectedRowKeys.length === 1 ? "warehouse" : "warehouses"} selected
              </Text>
            </div>
            <Space>
              <Button icon={<ExportOutlined />} onClick={handleBulkExport}>
                Export to CSV
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={handleBulkDelete}>
                Delete Selected
              </Button>
              <Button type="text" onClick={() => setSelectedRowKeys([])}>
                Clear Selection
              </Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredWarehouses}
          rowKey="id"
          loading={loading}
          onRow={(record) => ({
            onClick: (e) => {
              // Only handle clicks if not on the Actions column
              if (!e.target.closest(".ant-table-cell-fix-right")) {
                console.log("🖱️ Row clicked:", record.name);
                onWarehouseSelect(record);
              }
            }
          })}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: 25,
            pageSizeOptions: ["10", "25", "50", "100"]
          }}
          scroll={{ x: "max-content" }}
          size="small"
          className="warehouses-table"
        />
      </div>
    </div>
  );
};

export default WarehousesListTable;
